// Messages privés Réseau Social - Version avec mode démo/réel
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Send, Search, Phone, Video, MoreVertical, Image, Smile, CheckCheck, UserPlus, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const DEMO_CONVERSATIONS = [];

const DEMO_MESSAGES = [];

function ConversationItem({ conversation, isActive, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
        isActive ? 'bg-[#F0F4F8]' : 'hover:bg-gray-50'
      }`}
    >
      <div className="relative">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] rounded-full flex items-center justify-center text-white font-bold">
          {conversation.name.charAt(0)}
        </div>
        {conversation.online && (
          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 truncate">{conversation.name}</h3>
          <span className="text-xs text-gray-500">{conversation.time}</span>
        </div>
        <div className="flex items-center gap-1">
          {conversation.read && <CheckCheck size={14} className="text-blue-500" />}
          <p className="text-sm text-gray-500 truncate">{conversation.lastMessage}</p>
        </div>
      </div>
      {conversation.unread > 0 && (
        <span className="px-2 py-0.5 bg-[#1E3A5F] text-white text-xs font-bold rounded-full">
          {conversation.unread}
        </span>
      )}
    </div>
  );
}

function Message({ message, isOwn }) {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[70%] ${isOwn ? 'order-2' : ''}`}>
        <div className={`px-4 py-2 rounded-2xl ${
          isOwn 
            ? 'bg-[#1E3A5F] text-white rounded-br-sm' 
            : 'bg-gray-100 text-gray-900 rounded-bl-sm'
        }`}>
          <p className="text-sm">{message.text}</p>
        </div>
        <p className={`text-xs text-gray-400 mt-1 ${isOwn ? 'text-right' : ''}`}>
          {message.time}
        </p>
      </div>
    </div>
  );
}

export default function MessagesReseau() {
  const { user } = useCurrentUser();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const isDemo = user?.isDemo;

  useEffect(() => {
    if (user !== null) {
      if (isDemo) {
        setConversations(DEMO_CONVERSATIONS);
        setActiveConv(DEMO_CONVERSATIONS[0]);
        setMessages(DEMO_MESSAGES);
      } else {
        // Vrais utilisateurs : pas de conversations
        setConversations([]);
        setActiveConv(null);
        setMessages([]);
      }
      setLoading(false);
    }
  }, [user, isDemo]);

  const handleSend = () => {
    if (!newMessage.trim()) return;
    if (isDemo) {
      toast.error('Crée ton compte pour envoyer des messages !');
      return;
    }
    setMessages(prev => [...prev, {
      id: prev.length + 1,
      text: newMessage,
      time: 'À l\'instant',
      isOwn: true,
    }]);
    setNewMessage('');
  };

  const filteredConversations = conversations.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // État vide pour vrais utilisateurs
  if (!isDemo && conversations.length === 0) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Messages</h1>
            <p className="text-sm text-gray-500 mt-1">Tes conversations privées</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <Inbox size={32} className="text-blue-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune conversation</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Tu n'as pas encore de messages. Envoie un message à un membre de la communauté pour commencer !
          </p>
          <button className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors inline-flex items-center gap-2">
            <UserPlus size={18} />
            Trouver des membres
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] flex bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Banner démo */}
      {isDemo && (
        <div className="absolute top-0 left-0 right-0 bg-amber-50 border-b border-amber-200 px-4 py-2 z-10">
          <p className="text-sm text-amber-800 text-center">
            <span className="font-medium">Mode Démonstration</span> — Ces conversations sont des exemples
          </p>
        </div>
      )}

      {/* Liste des conversations */}
      <div className={`w-80 border-r border-gray-200 flex flex-col ${isDemo ? 'mt-10' : ''}`}>
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-900 mb-3">Messages</h2>
          <div className="flex items-center gap-2 px-2.5 py-1.5 border border-gray-200 rounded-lg">
            <Search size={14} className="text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-900 placeholder-gray-400"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {filteredConversations.map(conv => (
            <ConversationItem
              key={conv.id}
              conversation={conv}
              isActive={activeConv?.id === conv.id}
              onClick={() => {
                setActiveConv(conv);
                if (isDemo) setMessages(DEMO_MESSAGES);
              }}
            />
          ))}
        </div>
      </div>

      {/* Zone de chat */}
      {activeConv ? (
        <div className={`flex-1 flex flex-col ${isDemo ? 'mt-10' : ''}`}>
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] rounded-full flex items-center justify-center text-white font-bold">
                  {activeConv.name.charAt(0)}
                </div>
                {activeConv.online && (
                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{activeConv.name}</h3>
                <p className="text-xs text-gray-500">{activeConv.online ? 'En ligne' : 'Hors ligne'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <Phone size={18} className="text-gray-500" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <Video size={18} className="text-gray-500" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <MoreVertical size={18} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map(msg => (
              <Message key={msg.id} message={msg} isOwn={msg.isOwn} />
            ))}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <Image size={20} className="text-gray-500" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <Smile size={20} className="text-gray-500" />
              </button>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Écrire un message..."
                className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F]"
              />
              <button
                onClick={handleSend}
                className="p-2 bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white rounded-full"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className={`flex-1 flex items-center justify-center ${isDemo ? 'mt-10' : ''}`}>
          <div className="text-center">
            <MessageCircle size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Sélectionne une conversation</p>
          </div>
        </div>
      )}
    </div>
  );
}
